/**
 * Mobile Authentication Layout Template
 *
 * Shell used for pre-auth surfaces — Login, OAuth callback, Unauthorized.
 * Lime/midnight palette, mirrors the rider portal's in-app chrome so users
 * don't traverse a branding seam at the auth boundary.
 */

import type { FC, ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import styled, { keyframes } from 'styled-components';
import { r, riderLight } from '../../theme/riderTheme';

interface MobileAuthLayoutProps {
  children: ReactNode;
  eyebrow?: string;
  showFooter?: boolean;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Shell = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => r(theme).cta.bg};
  color: ${({ theme }) => r(theme).text.inverse};
  font-family: 'Geist', system-ui, sans-serif;
  font-feature-settings: 'ss01', 'cv11';
  overflow: hidden;

  /* Soft lime atmosphere — sits behind everything and provides depth without
     reading as a stock gradient. */
  &::before {
    content: '';
    position: absolute;
    top: -180px;
    left: -120px;
    width: 460px;
    height: 460px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(192, 223, 22, 0.16) 0%,
      rgba(192, 223, 22, 0.06) 38%,
      transparent 70%
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -160px;
    right: -100px;
    width: 380px;
    height: 380px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(0, 215, 255, 0.10) 0%,
      rgba(0, 215, 255, 0.04) 40%,
      transparent 70%
    );
    pointer-events: none;
  }
`;

const Brand = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: max(20px, env(safe-area-inset-top)) 20px 14px;
  z-index: 1;
`;

const BrandMark = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const BrandGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${({ theme }) => r(theme).accent.lime};
  box-shadow: 0 0 0 1px rgba(192, 223, 22, 0.5);

  & > svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

const BoltMark = () => (
  <svg viewBox="0 0 160 160" aria-hidden focusable="false">
    <path d="M115 35 72 82h28l-42 43 18-35H54l42-55h19z" fill="#121215" />
  </svg>
);

const BrandWord = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.inverse};
`;

const BrandEyebrow = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const HairLine = styled.div`
  position: relative;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${({ theme }) => r(theme).accent.limeDeep} 18%,
    ${({ theme }) => r(theme).accent.lime} 50%,
    ${({ theme }) => r(theme).accent.limeDeep} 82%,
    transparent 100%
  );
  opacity: 0.55;
  margin: 0 16px;
  z-index: 1;
`;

const Stage = styled.main`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
  z-index: 1;
  animation: ${fadeIn} 360ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const Footer = styled.footer`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px max(20px, env(safe-area-inset-bottom));
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.42);
  z-index: 1;
`;

const FooterDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ theme }) => r(theme).accent.lime};
    box-shadow: 0 0 8px ${({ theme }) => r(theme).accent.lime};
  }
`;

const MobileAuthLayout: FC<MobileAuthLayoutProps> = ({
  children,
  eyebrow = 'Gogoro Nepal',
  showFooter = true,
}) => {
  return (
    <ThemeProvider theme={riderLight as never}>
      <Shell>
        <Brand>
          <BrandMark>
            <BrandGlyph aria-hidden>
              <BoltMark />
            </BrandGlyph>
            <BrandWord>Rider Gateway</BrandWord>
          </BrandMark>
          <BrandEyebrow>{eyebrow}</BrandEyebrow>
        </Brand>
        <HairLine />

        <Stage>{children}</Stage>

        {showFooter && (
          <Footer>
            <FooterDot>Secure session</FooterDot>
            <span>© LoopX</span>
          </Footer>
        )}
      </Shell>
    </ThemeProvider>
  );
};

export default MobileAuthLayout;
